import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
// Lazy-load Login so initial bundle is smaller and login page loads faster
const Login = lazy(() => import('./pages/Login'));
// WhatsApp one-page booking / slip form — public, opened from a tokenised link
const WhatsAppForm = lazy(() => import('./pages/WhatsAppForm'));
// Lazy-load pages so initial load after login is faster
const Dashboard = lazy(() => import('./pages/DashboardWorkspace'));
const PurchaseBooking = lazy(() => import('./pages/PurchaseBooking'));
const PreProcessing = lazy(() => import('./pages/PreProcessing'));
const RawMaterialPool = lazy(() => import('./pages/RawMaterialPool'));
const FinishedGoods = lazy(() => import('./pages/FinishedGoods'));
const MerchantHub = lazy(() => import('./pages/MerchantHub'));
const Agents = lazy(() => import('./pages/Agents'));
const Notifications = lazy(() => import('./pages/Notifications'));
const QualityControl = lazy(() => import('./pages/QualityControl'));
const UniformityQC = lazy(() => import('./pages/UniformityQC'));
const UniformityDashboard = lazy(() => import('./pages/UniformityDashboard'));
const UniformityMasters = lazy(() => import('./pages/UniformityMasters'));
const ColdStorage = lazy(() => import('./pages/ColdStorage'));
const ColdStoreIntake = lazy(() => import('./pages/ColdStoreIntake'));
const Sales = lazy(() => import('./pages/Sales'));
const Accounts = lazy(() => import('./pages/AccountsHrm'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const DefectMasters = lazy(() => import('./pages/DefectMasters'));
const SizeGrades = lazy(() => import('./pages/SizeGrades'));
const FloorBalance = lazy(() => import('./pages/FloorBalance'));
const PackingStyles = lazy(() => import('./pages/PackingStyles'));
const StoreMovements = lazy(() => import('./pages/StoreMovements'));
const ColdStorageMasters = lazy(() => import('./pages/ColdStorageMasters'));
const Inventory = lazy(() => import('./pages/Inventory'));
const ReverseCalculator = lazy(() => import('./pages/ReverseCalculator'));
const RmRequirement = lazy(() => import('./pages/RmRequirement'));
const LotPnl = lazy(() => import('./pages/LotPnl'));
const CostAnalyser = lazy(() => import('./pages/CostAnalyser'));
const MarginCommandCenter = lazy(() => import('./pages/MarginCommandCenter'));
const CostComponents = lazy(() => import('./pages/CostComponents'));
const ClientIntelligence = lazy(() => import('./pages/ClientIntelligence'));
const PriceBenchmarking = lazy(() => import('./pages/PriceBenchmarking'));
const FormYields = lazy(() => import('./pages/FormYields'));
const QCLab = lazy(() => import('./pages/QCLab'));
const QCGates = lazy(() => import('./pages/QCGates'));
const QCMasters = lazy(() => import('./pages/QCMasters'));
const QCCompliance = lazy(() => import('./pages/QCCompliance'));
const QCAudits = lazy(() => import('./pages/QCAudits'));
const EIACertificate = lazy(() => import('./pages/EIACertificate'));
const Employees = lazy(() => import('./pages/Employees'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Payroll = lazy(() => import('./pages/Payroll'));
const Expenses = lazy(() => import('./pages/Expenses'));
const SpecMasters = lazy(() => import('./pages/SpecMasters'));
const YieldBenchmarks = lazy(() => import('./pages/YieldBenchmarks'));
const YieldAccuracy = lazy(() => import('./pages/YieldAccuracy'));
const CycleTimes = lazy(() => import('./pages/CycleTimes'));
const GradeMixCalculator = lazy(() => import('./pages/GradeMixCalculator'));
const PurchaseVsProduction = lazy(() => import('./pages/PurchaseVsProduction'));
const WastageDashboard = lazy(() => import('./pages/WastageDashboard'));
const LotLifecycle = lazy(() => import('./pages/LotLifecycle'));
const LotBoard = lazy(() => import('./pages/LotBoard'));
const Traceability = lazy(() => import('./pages/Traceability'));
const ProcurementBatches = lazy(() => import('./pages/ProcurementBatches'));
const Grading = lazy(() => import('./pages/Grading'));
const PhaseEntry = lazy(() => import('./pages/PhaseEntry'));
const PhaseReconcile = lazy(() => import('./pages/PhaseReconcile'));
const Reports = lazy(() => import('./pages/Reports'));
const AnalysisReportTemplate = lazy(() => import('./pages/AnalysisReportTemplate'));
const BatchPipeline = lazy(() => import('./pages/BatchPipeline'));
const PreProcessingWorkflow = lazy(() => import('./pages/PreProcessingWorkflow'));
const PreProcessingHome = lazy(() => import('./pages/PreProcessingHome'));
const ProductionWorkflow = lazy(() => import('./pages/ProductionWorkflow'));
const ProductionEntry = lazy(() => import('./pages/ProductionEntry'));
const ProductionMasters = lazy(() => import('./pages/ProductionMasters'));
const PreProductionMasters = lazy(() => import('./pages/PreProductionMasters'));
const MarketRates = lazy(() => import('./pages/MarketRates'));
const LotWaterfall = lazy(() => import('./pages/LotWaterfall'));
const CreateWageBill = lazy(() => import('./pages/CreateWageBill'));
const WageBillDetail = lazy(() => import('./pages/WageBillDetail'));
const AuditTrail = lazy(() => import('./pages/AuditTrail'));
const DataMigration = lazy(() => import('./pages/DataMigration'));
const MyChangeRequests = lazy(() => import('./pages/MyChangeRequests'));
const PurchaseInvoices = lazy(() => import('./pages/PurchaseInvoices'));
const PurchaseInvoiceForm = lazy(() => import('./pages/PurchaseInvoiceForm'));
const PurchaseRiskAlerts = lazy(() => import('./pages/PurchaseRiskAlerts'));
const Parties = lazy(() => import('./pages/Parties'));
const VehicleCompliance = lazy(() => import('./pages/VehicleCompliance'));
const Masters = lazy(() => import('./pages/Masters'));
const Docs = lazy(() => import('./pages/Docs'));
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { BrandingProvider } from './context/BrandingContext';
import { AlertProvider } from './context/AlertContext';
import { MoneyProvider } from './context/MoneyContext';
import AppToaster from './components/native/PhoneMessages';
import { LangProvider } from './components/shared/i18n';
import { canAccessDashboard } from './config/moduleConfig';
import { BACKEND_URL, IS_NATIVE, WORKSPACE_ORIGIN } from './config/runtime';
import NativeWorkspace from './pages/NativeWorkspace';
import { LEGAL_ROUTES } from './pages/LegalPages';
import NativeShell from './components/native/NativeShell';
import PhaseBlindGate from './components/preprocessing/PhaseBlindGate';
import PhaseRedirect from './components/preprocessing/PhaseRedirect';
import './App.css';

const SuperAdminRedirect = lazy(() => import('./pages/SuperAdminRedirect'));
const WorkspaceFinder = lazy(() => import('./pages/WorkspaceFinder'));

// Apex hosts that should show the workspace-finder landing page instead of a
// tenant login. Comma-separated, configurable via env. Defaults to the prod
// apex; local dev (localhost) still loads a tenant context so the existing
// flow keeps working.
const APEX_HOSTS = (process.env.REACT_APP_APEX_HOSTS || 'aquaerp.in,www.aquaerp.in')
  .split(',')
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);
const ROOT_DOMAIN = process.env.REACT_APP_ROOT_DOMAIN || 'aquaerp.in';


// Inventory is one page now (pages/Inventory.js). Its old screens used `?tab=` for
// their own sub-tabs, which the page now reads as `?view=`; notifications already
// sent carry those links, so the old value is carried across rather than dropped.
const INVENTORY_LEGACY_VIEW = {
  vendors: { tab: 'vendors' },
  register: { tab: 'stock', view: 'register' },
  signature: { settings: 'signature' },
  settings: { settings: 'po' },
};
function RedirectToInventory({ tab, extra }) {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  const old = params.get('tab');
  params.set('tab', tab);
  if (old) {
    const mapped = INVENTORY_LEGACY_VIEW[old];
    if (mapped) Object.entries(mapped).forEach(([k, v]) => params.set(k, v));
    else if (!params.has('view')) params.set('view', old);
  }
  Object.entries(extra || {}).forEach(([k, v]) => { if (!params.has(k)) params.set(k, v); });
  return <Navigate to={`/inventory?${params.toString()}${hash || ''}`} replace />;
}

// The former Overview workspace is now the Production Day Overview dashboard tab.
function RedirectToDashboardOverview() {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  params.set('dashboardTab', 'overview');
  params.delete('tab');
  return <Navigate to={`/?${params.toString()}${hash || ''}`} replace />;
}

function isApexHost() {
  // Explicit override for local testing: ?finder=1 forces the workspace finder
  if (new URLSearchParams(window.location.search).get('finder') === '1') return true;
  return APEX_HOSTS.includes(window.location.hostname.toLowerCase());
}

// Loading fallback for lazy components
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
  </div>
);

// Minimal fallback for login route so login page appears fast
const LoginFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-300 border-t-blue-600" />
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingFallback />;
  }
  return user ? children : <Navigate to="/login" replace />;
};

const DashboardRoute = () => {
  const { user } = useAuth();
  
  // Redirect super_admin to the "use standalone portal" page (Option 2: no in-app panel)
  if (user?.role === 'super_admin') {
    return <Navigate to="/platform-admin" replace />;
  }
  
  // Check if user has dashboard access
  if (user && canAccessDashboard(user.role)) {
    return <Dashboard />;
  }
  
  // Redirect workers to their first accessible page
  return <Navigate to="/procurement" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Suspense fallback={<LoginFallback />}><Login /></Suspense>} />
      {/* No login: the link's token is the identity (see pages/WhatsAppForm.js) */}
      <Route path="/wa/:token" element={<Suspense fallback={<LoginFallback />}><WhatsAppForm /></Suspense>} />
      {/* Redirect to standalone Super Admin portal (Option 2: no in-app panel) */}
      <Route path="/super-admin" element={<Navigate to="/super-admin/login" replace />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardRoute />} />
        <Route path="procurement" element={<RedirectToDashboardOverview />} />
        <Route path="procurement-batches" element={<PhaseBlindGate><ProcurementBatches /></PhaseBlindGate>} />
        <Route path="reports" element={<Reports />} />
        <Route path="masters" element={<Masters />} />
        <Route path="masters/:section" element={<Masters />} />
        <Route path="purchase-booking" element={<PurchaseBooking />} />
        <Route path="agents" element={<Agents />} />
        <Route path="preprocessing/home" element={<PhaseRedirect to="/procurement"><PreProcessingHome /></PhaseRedirect>} />
        {/* Phase teams on: the combined screens redirect to the station / Lot Check that replaced them. */}
        <Route path="preprocessing" element={<PhaseRedirect to="/procurement-batches"><PhaseBlindGate><PreProcessing /></PhaseBlindGate></PhaseRedirect>} />
        {/* The Raw Material Pool was a tab on Pre-Processing; it is its own page now. */}
        <Route path="raw-material-pool" element={<PhaseBlindGate allowStages={['procurement']}><RawMaterialPool /></PhaseBlindGate>} />
        <Route path="preprocessing/defect-masters" element={<DefectMasters />} />
        <Route path="preprocessing/size-grades" element={<SizeGrades />} />
        <Route path="preprocessing/grading" element={<PhaseRedirect to="/preprocessing/phases/grading"><PhaseBlindGate><Grading /></PhaseBlindGate></PhaseRedirect>} />
        <Route path="preprocessing/phases" element={<PhaseEntry />} />
        <Route path="preprocessing/phases/:phase" element={<PhaseEntry />} />
        <Route path="preprocessing/lot-check" element={<PhaseReconcile />} />
        <Route path="preprocessing/reconcile" element={<Navigate to="/preprocessing/lot-check" replace />} />
        <Route path="preprocessing/masters" element={<PreProductionMasters />} />
        <Route path="preprocessing/floor-balance" element={<PhaseRedirect to="/preprocessing/lot-check?view=balance"><FloorBalance /></PhaseRedirect>} />
        <Route path="preprocessing/analysis-report-template" element={<AnalysisReportTemplate />} />
        <Route path="preprocessing/workflow" element={<PhaseRedirect to="/?dashboardTab=overview"><PhaseBlindGate><PreProcessingWorkflow /></PhaseBlindGate></PhaseRedirect>} />
        {/* The former Overview workspace now lives on Dashboard. */}
        <Route path="workflow" element={<Navigate to="/?dashboardTab=overview" replace />} />
        <Route path="batch-pipeline" element={<BatchPipeline />} />
        <Route path="production" element={<Navigate to="/?dashboardTab=overview" replace />} />
        <Route path="production/workflow" element={<ProductionWorkflow />} />
        <Route path="production/entry" element={<ProductionEntry />} />
        <Route path="production/masters" element={<ProductionMasters />} />
        <Route path="production/packing-styles" element={<PackingStyles />} />
        <Route path="finished-goods" element={<FinishedGoods />} />
        <Route path="merchants" element={<MerchantHub />} />
        {/* Moved into the Reports tab. Kept as a redirect so bookmarks, global search
            and the assistant's deep links still land on the report. */}
        <Route path="analytics/hon-to-finished" element={<Navigate to="/reports?report=hon-to-finished" replace />} />
        <Route path="analytics/reverse-rm" element={<ReverseCalculator />} />
        <Route path="analytics/rm-requirement" element={<RmRequirement />} />
        <Route path="analytics/lot-pnl" element={<LotPnl />} />
        <Route path="analytics/cost-analyser" element={<CostAnalyser />} />
        <Route path="analytics/margin-command-center" element={<MarginCommandCenter />} />
        <Route path="analytics/yield-accuracy" element={<YieldAccuracy />} />
        <Route path="analytics/cycle-times" element={<CycleTimes />} />
        <Route path="analytics/grade-mix-calculator" element={<GradeMixCalculator />} />
        <Route path="analytics/purchase-vs-production" element={<PurchaseVsProduction />} />
        <Route path="analytics/cost-components" element={<CostComponents />} />
        {/* Procurement Alerts now lives as a tab on Sales & Dispatch — keep old links working. */}
        <Route path="analytics/procurement-alerts" element={<Navigate to="/sales" replace />} />
        <Route path="analytics/form-yields" element={<FormYields />} />
        <Route path="analytics/spec-masters" element={<SpecMasters />} />
        <Route path="qc" element={<QualityControl />} />
        <Route path="qc/lab" element={<QCLab />} />
        <Route path="qc/gates" element={<QCGates />} />
        <Route path="qc/uniformity" element={<UniformityQC />} />
        <Route path="qc/uniformity/dashboard" element={<UniformityDashboard />} />
        <Route path="qc/uniformity/masters" element={<UniformityMasters />} />
        <Route path="qc/masters" element={<QCMasters />} />
        <Route path="qc/compliance" element={<QCCompliance />} />
        <Route path="qc/audits" element={<QCAudits />} />
        <Route path="qc/eia" element={<EIACertificate />} />
        <Route path="plant-overview" element={<Navigate to="/?dashboardTab=plant-overview" replace />} />
        <Route path="cold-storage" element={<ColdStorage />} />
        <Route path="cold-storage/intake" element={<ColdStoreIntake />} />
        <Route path="cold-storage/masters" element={<ColdStorageMasters />} />
        {/* Both boards were folded into screens that already answer the question:
            the cold-storage lifecycle is the last band of the Workflow Board, and the
            chamber map is a tab on Cold Storage Insights. Old links still land. */}
        <Route path="cold-storage/workflow" element={<Navigate to="/?dashboardTab=overview" replace />} />
        <Route path="cold-storage/store-view" element={<Navigate to="/cold-storage?view=store" replace />} />
        <Route path="cold-storage/movements" element={<StoreMovements />} />
        {/* Inventory is one page with tabs (Stock, Indents, Purchase, Vendors,
            Costing). Every old path redirects into it, keeping ?focus= — notifications
            already sent carry links like /inventory/purchase-orders?tab=orders&focus=<id>. */}
        <Route path="inventory" element={<Inventory />} />
        <Route path="inventory/requests" element={<RedirectToInventory tab="indents" />} />
        <Route path="inventory/indents" element={<RedirectToInventory tab="indents" />} />
        <Route path="inventory/purchasing" element={<RedirectToInventory tab="purchase" />} />
        <Route path="inventory/purchase-orders" element={<RedirectToInventory tab="purchase" />} />
        <Route path="inventory/vendors" element={<RedirectToInventory tab="vendors" />} />
        <Route path="inventory/items" element={<RedirectToInventory tab="stock" />} />
        <Route path="inventory/sheet" element={<RedirectToInventory tab="stock" extra={{ view: 'register' }} />} />
        <Route path="inventory/costing" element={<RedirectToInventory tab="costing" />} />
        <Route path="sales" element={<Sales />} />
        <Route path="clients/intelligence" element={<ClientIntelligence />} />
        <Route path="analytics/price-benchmarking" element={<PriceBenchmarking />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="accounts/create" element={<CreateWageBill />} />
        <Route path="accounts/:billId" element={<WageBillDetail />} />
        <Route path="admin" element={<AdminPanel />} />
        <Route path="admin/yield-benchmarks" element={<YieldBenchmarks />} />
        <Route path="admin/market-rates" element={<MarketRates />} />
        <Route path="admin/wastage-dashboard" element={<WastageDashboard />} />
        <Route path="analytics/lot-lifecycle" element={<LotLifecycle />} />
        <Route path="analytics/lot-board" element={<LotBoard />} />
        <Route path="traceability" element={<Traceability />} />
        <Route path="admin/lot/:lotId/wastage" element={<LotWaterfall />} />
        <Route path="admin/audit-trail" element={<AuditTrail />} />
        <Route path="data-migration" element={<DataMigration />} />
        {/* Deliberately not under /admin: this is the maker's own view of their
            own held changes, and `worker` is in neither the auditTrail nor the
            supervision role matrix. */}
        <Route path="my-change-requests" element={<MyChangeRequests />} />
        <Route path="purchase-invoices" element={<PurchaseInvoices />} />
        <Route path="purchase-invoices/create" element={<PurchaseInvoiceForm />} />
        <Route path="purchase-invoices/edit/:id" element={<PurchaseInvoiceForm />} />
        <Route path="purchase-risk-alerts" element={<PurchaseRiskAlerts />} />
        <Route path="risk-area-insights" element={<Navigate to="/purchase-risk-alerts?tab=insights" replace />} />
        {/* Party Master and Party Ledger are tabs of one page now — the two screens
            described the same people. Old links still land. */}
        <Route path="parties" element={<Parties />} />
        <Route path="party-ledger" element={<Navigate to="/parties?tab=ledger" replace />} />
        <Route path="hr/employees" element={<Employees />} />
        <Route path="hr/attendance" element={<Attendance />} />
        <Route path="hr/payroll" element={<Payroll />} />
        <Route path="hr/expenses" element={<Expenses />} />
        {/* The per-party URL never selected a party — the page has always opened on
            its own list — so it lands on the same tab. */}
        <Route path="party-ledger/:partyId" element={<Navigate to="/parties?tab=ledger" replace />} />
        <Route path="vehicle-compliance" element={<VehicleCompliance />} />
        {/* Company Settings is now a tab of the Admin Panel. */}
        <Route path="admin/company-settings" element={<Navigate to="/admin?tab=company" replace />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="docs" element={<Docs />} />
        <Route path="docs/:slug" element={<Docs />} />
        <Route path="platform-admin" element={<SuperAdminRedirect />} />
      </Route>
    </Routes>
  );
}

function TenantGuard({ children }) {
  // For non-apex hosts, verify the subdomain actually maps to a workspace
  // BEFORE mounting the tenant providers. If not, redirect to the apex finder
  // (so visiting "typo.aquaerp.in" doesn't dump the user into a broken-looking
  // session or, worse, a leaked default-tenant view).
  const [state, setState] = React.useState({ status: 'checking' });
  React.useEffect(() => {
    const host = window.location.hostname;
    fetch(`${BACKEND_URL}/api/public/check-workspace?host=${encodeURIComponent(host)}`)
      .then((r) => {
        if (r.ok) { setState({ status: 'ok' }); return; }
        if (r.status === 404) { setState({ status: 'not_found' }); return; }
        // Other errors (502/network) — fail open so a transient backend hiccup
        // doesn't lock real users out of their workspace.
        setState({ status: 'ok' });
      })
      .catch(() => setState({ status: 'ok' }));
  }, []);
  if (state.status === 'checking') return <LoginFallback />;
  if (state.status === 'not_found') {
    // Bounce to the apex finder; preserve scheme + port for local dev.
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : '';
    window.location.replace(`${protocol}//${ROOT_DOMAIN}${port}/`);
    return <LoginFallback />;
  }
  return children;
}

function App() {
  // Privacy policy / account deletion: public, on every host and in the app,
  // ahead of every workspace and sign-in check — the store reviewers who must
  // be able to open them have no workspace.
  const LegalPage = LEGAL_ROUTES[window.location.pathname.replace(/\/+$/, '')];
  if (LegalPage) {
    return <LegalPage />;
  }

  // The store app is served from the phone (localhost), so neither the apex
  // finder nor the subdomain guard means anything there: it asks for the
  // workspace once (pages/NativeWorkspace.js) and every request goes to it.
  if (IS_NATIVE && !WORKSPACE_ORIGIN) {
    return <NativeWorkspace />;
  }

  // At the apex (aquaerp.in / www.aquaerp.in), skip all tenant providers and
  // render the workspace finder. This stops the page from making API calls
  // that would otherwise pick up cli_001 (Sunbitees) via the host-fallback.
  if (!IS_NATIVE && isApexHost()) {
    return (
      <Suspense fallback={<LoginFallback />}>
        <WorkspaceFinder rootDomain={ROOT_DOMAIN} />
      </Suspense>
    );
  }

  // The workspace was verified when it was chosen on the phone.
  const Guard = IS_NATIVE ? React.Fragment : TenantGuard;

  return (
    <Guard>
    <BrowserRouter>
      {IS_NATIVE ? <NativeShell /> : null}
      <BrandingProvider>
        <AuthProvider>
          <FeatureFlagProvider>
            <LangProvider>
              <MoneyProvider>
              <AlertProvider>
                <AppRoutes />
                {/* Top-right on desktop, bottom snackbar + inline sheet errors on a phone. */}
                <AppToaster />
              </AlertProvider>
              </MoneyProvider>
            </LangProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
    </Guard>
  );
}

export default App;
